import React, { useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, PageHeader, StatusBadge } from '../../components/ui';
import { byId, fullName, getCourse, getLessonsForVersion, userIsCourseTrainer } from '../../utils/lmsSelectors';

function ExerciseReviewActions({ row, currentUser, reviewExerciseSubmission }) {
  const [score, setScore] = useState(row.maxScore || 100);
  const [feedback, setFeedback] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setMessage('Saving grade...');
    const result = await reviewExerciseSubmission(row.assignmentId, row.submissionId, currentUser.id, { score, feedback });
    setSaving(false);
    setMessage(result.ok ? 'Exercise graded.' : result.message);
  };

  return (
    <div className="review-action-stack">
      <div className="review-score-row">
        <input type="number" min="0" max={row.maxScore || 100} value={score} onChange={(event) => setScore(event.target.value)} aria-label="Exercise score" />
        <span className="small muted">/ {row.maxScore || 100}</span>
      </div>
      <textarea rows="2" placeholder="Feedback" value={feedback} onChange={(event) => setFeedback(event.target.value)} />
      <button className="button button-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Submit grade'}</button>
      {message && <span className="small muted">{message}</span>}
    </div>
  );
}

function AttendanceActions({ row, currentUser, approveLessonSubmission, rejectLessonSubmission }) {
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    setMessage('Saving attendance...');
    const result = await approveLessonSubmission(row.assignmentId, row.lessonId, currentUser.id, 'Attendance confirmed.');
    setSaving(false);
    setMessage(result.ok ? 'Attendance confirmed.' : result.message);
  };

  const notAttended = async () => {
    if (saving) return;
    setSaving(true);
    setMessage('Saving attendance note...');
    const result = await rejectLessonSubmission(row.assignmentId, row.lessonId, currentUser.id, 'Attendance not confirmed.');
    setSaving(false);
    setMessage(result.ok ? 'Marked not attended.' : result.message);
  };

  return (
    <div className="button-row compact">
      <button className="button button-primary" onClick={confirm} disabled={saving}>{saving ? 'Saving...' : 'Confirm attendance'}</button>
      <button className="button button-ghost" onClick={notAttended} disabled={saving}>Not attended</button>
      {message && <span className="small muted">{message}</span>}
    </div>
  );
}

export default function TrainerReviews() {
  const { data, currentUser, approveLessonSubmission, rejectLessonSubmission, reviewExerciseSubmission } = useLms();
  const trainerCourseIds = data.courses.filter((course) => currentUser.role === 'admin' || userIsCourseTrainer(data, course, currentUser.id)).map((course) => course.id);
  const reviewAssignments = data.assignments.filter((assignment) => assignment.status !== 'revoked' && trainerCourseIds.includes(assignment.courseId));

  const practicalRows = reviewAssignments.flatMap((assignment) => getLessonsForVersion(data, assignment.courseVersionId)
    .filter((lesson) => lesson.lessonType === 'practical' && lesson.isRequired !== false)
    .map((lesson) => {
      const progress = assignment.lessonProgress?.[lesson.id] || { status: 'not_started' };
      if (progress.status === 'completed') return null;
      const learner = byId(data.users, assignment.assignedToUserId);
      const course = getCourse(data, assignment.courseId);
      return {
        id: `${assignment.id}-${lesson.id}`,
        type: 'Practical Activity',
        assignmentId: assignment.id,
        lessonId: lesson.id,
        learner: fullName(learner),
        course: course?.title,
        item: lesson.title,
        submittedAt: progress.submittedAt ? new Date(progress.submittedAt).toLocaleString() : '—',
        submissionText: progress.submissionText || (lesson.sessionDate ? `Session: ${new Date(lesson.sessionDate).toLocaleString()}` : 'Session date not set'),
        submissionUrl: lesson.meetingLink || progress.submissionUrl || '',
        status: progress.status === 'needs_review' ? 'needs_review' : 'attendance_pending'
      };
    })
    .filter(Boolean));

  const exerciseRows = reviewAssignments.flatMap((assignment) => (assignment.exerciseSubmissions || [])
    .filter((submission) => submission.status === 'needs_review')
    .map((submission) => {
      const learner = byId(data.users, assignment.assignedToUserId);
      const exercise = byId(data.exercises || [], submission.exerciseId);
      const course = getCourse(data, assignment.courseId);
      return {
        id: `${assignment.id}-${submission.id}`,
        type: 'Exercise',
        assignmentId: assignment.id,
        submissionId: submission.id,
        learner: fullName(learner),
        course: course?.title,
        item: exercise?.title,
        maxScore: exercise?.maxScore || 100,
        submittedAt: submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : '—',
        submissionText: submission.submissionText,
        submissionUrl: submission.submissionUrl || '',
        status: submission.status
      };
    }));

  const rows = [...practicalRows, ...exerciseRows].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

  return (
    <>
      <PageHeader title="Manual review queue" description="Confirm practical activity attendance and check/grade learner exercise submissions required for course completion." />
      <Card>
        <DataTable
          rows={rows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'course', label: 'Course' },
            { key: 'item', label: 'Item', render: (row) => <div><strong>{row.item}</strong><p className="muted small">{row.type}</p></div> },
            { key: 'submissionText', label: 'Details', render: (row) => (
              <div className="submission-cell">
                {row.submissionUrl && <a href={row.submissionUrl} target="_blank" rel="noreferrer">{row.type === 'Practical Activity' ? 'Open meeting link' : 'Open submitted Drive link'}</a>}
                {row.submissionText && <p className="muted small">{row.submissionText}</p>}
                {!row.submissionUrl && !row.submissionText && '—'}
              </div>
            ) },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'actions', label: 'Actions', render: (row) => row.type === 'Exercise' ? (
              <ExerciseReviewActions row={row} currentUser={currentUser} reviewExerciseSubmission={reviewExerciseSubmission} />
            ) : (
              <AttendanceActions row={row} currentUser={currentUser} approveLessonSubmission={approveLessonSubmission} rejectLessonSubmission={rejectLessonSubmission} />
            ) }
          ]}
          emptyMessage="No practical activities or exercises need trainer action."
        />
      </Card>
    </>
  );
}
